import React from 'react';
import styled from 'styled-components';
import { MicrostoreShelfTemplateFormValues } from './FormHelper/DataMapper';
import { LocationCell } from './LocationCell';

const SHELF_BACKGROUND_COLORS = ['#fff7ef', '#f3ffef', '#eff6ff'];

type ShelfGridProps = {
    shelf: MicrostoreShelfTemplateFormValues;
    shelfIndex: number;
    selectedShelfIndex: number | null;
    selectedLocationPosition: { column: number; row: number } | null;
    onLocationClick: (shelfIndex: number, rowIndex: number, columnIndex: number) => void;
    onShelfClick: (shelfIndex: number) => void;
};

export const ShelfGrid = ({
    shelf,
    shelfIndex,
    selectedShelfIndex,
    selectedLocationPosition,
    onLocationClick,
    onShelfClick,
}: ShelfGridProps) => {
    const backgroundColor = SHELF_BACKGROUND_COLORS[shelfIndex % SHELF_BACKGROUND_COLORS.length];
    const isShelfSelected = selectedShelfIndex === shelfIndex && selectedLocationPosition === null;

    return (
        <ShelfContainer $isSelected={isShelfSelected} $backgroundColor={backgroundColor}>
            <ShelfHeader onClick={() => onShelfClick(shelfIndex)} $isSelected={isShelfSelected}>
                <ShelfTitle>
                    Étagère {shelfIndex + 1}
                    {shelf.locationTemplateTable.length > 0 &&
                        ` (${shelf.locationTemplateTable[0]?.length || 0} × ${shelf.locationTemplateTable.length})`}
                </ShelfTitle>
            </ShelfHeader>

            {shelf.locationTemplateTable.map((locationRow, rowIndex) => (
                <LocationRow key={rowIndex}>
                    {locationRow.map((location, columnIndex) => {
                        const isSelected =
                            selectedShelfIndex === shelfIndex &&
                            selectedLocationPosition?.column === columnIndex &&
                            selectedLocationPosition?.row === rowIndex;

                        return (
                            <LocationCell
                                key={columnIndex}
                                location={location}
                                isSelected={isSelected}
                                onClick={() => onLocationClick(shelfIndex, rowIndex, columnIndex)}
                                shelfIndex={shelfIndex}
                                rowIndex={rowIndex}
                                columnIndex={columnIndex}
                            />
                        );
                    })}
                </LocationRow>
            ))}
        </ShelfContainer>
    );
};

const ShelfContainer = styled.div<{ $isSelected: boolean; $backgroundColor: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    border: 2px solid ${({ theme, $isSelected }) => ($isSelected ? theme.ctaPrimaryColor : theme.darkBorderColor)};
    box-shadow: ${({ theme, $isSelected }) => ($isSelected ? theme.boxShadow : 'none')};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 8px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    gap: 6px;
`;

const ShelfHeader = styled.div<{ $isSelected: boolean }>`
    cursor: pointer;
    padding: 8px 12px;
    background-color: ${({ theme, $isSelected }) => ($isSelected ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    color: ${({ theme, $isSelected }) => ($isSelected ? '#fff' : theme.textColor)};
    border-radius: ${({ theme }) => theme.borderRadius};
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ theme, $isSelected }) => ($isSelected ? theme.ctaPrimaryColor : theme.darkBorderColor)};
    }
`;

const ShelfTitle = styled.div`
    text-align: center;
    font-size: 14px;
`;

const LocationRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
`;
